// QuoteContext.js
import React, {createContext, useContext, useEffect, useState} from 'react';

const QuoteContext = createContext();

export const useQuote = () => useContext(QuoteContext);

export const QuoteProvider = ({ children, defaultMode = 'Auto' }) => {
   const [tabValue, setTabValue] = useState(0); // Assuming 'Auto' as default
   const [quotes, setQuotes] = useState([]);
   const [didSearch, setDidSearch] = useState(false);
   const [isPosting, setIsPosting] = useState(false);
   const [pickupLocation, setPickupLocation] = useState();
   const [dropoffLocation, setDropoffLocation] = useState();
   const [vehicleVIN, setVehicleVIN] = useState('');
   const [vehicleType, setVehicleType] = useState('');
   const [ vehicleYear, setVehicleYear ] = useState(null);
   const [ vehicleMake, setVehicleMake ] = useState('');
   const [ vehicleModel, setVehicleModel ] = useState('');
   const [ vehicleMakeModel, setVehicleMakeModel ] = useState('');
   const [vesselType, setVesselType] = useState('');
   const [deliverySpeed, setDeliverySpeed] = useState('standard');
   const [insurance, setInsurance] = useState('standard');
   const [transportType, setTransportType] = useState('open');
   const [vehicleCondition, setVehicleCondition] = useState(true);

   const [dimensions, setDimensions] = useState({ length: null, width: null, height: null, beamWidth: null });
   const [desiredPickupDate, setDesiredPickupDate] = useState('');
   const [desiredDeliveryDate, setDesiredDeliveryDate] = useState('');
   const [recurring, setRecurring] = useState({
      isRecurring: false,
      frequency: '',
      start_date: '',
      end_date: '',
   });
   const [materialType, setMaterialType] = useState('');
   const [materialGrade, setMaterialGrade] = useState('');
   const [showInsuranceValue, setShowInsuranceValue] = useState(false);
   const [insuranceRequired, setInsuranceRequired] = useState(false);
   const [insuranceValue, setInsuranceValue] = useState(false);
   const [hazardousMaterials, setHazardousMaterials] = useState(false);
   const [specialHandling, setSpecialHandling] = useState([]);
   const [pickupContactPhoneNumber, setPickupContactPhoneNumber] = useState(null);
   const [pickupContactEmail, setPickupContactEmail] = useState('');
   const [dropoffContactPhoneNumber, setDropoffContactPhoneNumber] = useState(null);
   const [dropoffContactEmail, setDropoffContactEmail] = useState('');
   const [pickupDateTime, setPickupDateTime] = useState(new Date());
   const [dropoffDateTime, setDropoffDateTime] = useState(new Date());

   const [vehicles, setVehicles] = useState([]);

   const [snackbar, setSnackbar] = useState({
      open: false,
      message: '',
      severity: 'info',
   });
   // Load saved form data from localStorage
   useEffect(() => {
      const savedDraft = localStorage.getItem('quoteDraft');
      
      if (savedDraft) {
         try {
            const parsedDraft = JSON.parse(savedDraft);
            
            // Set all the state from the saved draft
            setTabValue(parsedDraft.tabValue || 0);
            setQuotes(parsedDraft.quotes || []);
            setDidSearch(parsedDraft.didSearch || false);
            setIsPosting(parsedDraft.isPosting || false);
            setPickupLocation(parsedDraft.pickupLocation || '');
            setDropoffLocation(parsedDraft.dropoffLocation || '');
            setVehicleVIN(parsedDraft.vehicleVIN || '');
            setVehicleType(parsedDraft.vehicleType || '');
            setVehicleYear(parsedDraft.vehicleYear || null);
            setVehicleMake(parsedDraft.vehicleMake || '');
            setVehicleModel(parsedDraft.vehicleModel || '');
            setVehicleMakeModel(parsedDraft.vehicleMakeModel || '');
            setVesselType(parsedDraft.vesselType || '');
            setDeliverySpeed(parsedDraft.deliverySpeed || 'standard');
            setInsurance(parsedDraft.insurance || 'standard');
            setTransportType(parsedDraft.transportType || 'open');
            setVehicleCondition(parsedDraft.vehicleCondition || true);
            setDimensions(parsedDraft.dimensions || { length: null, width: null, height: null, beamWidth: null });
            setDesiredPickupDate(parsedDraft.desiredPickupDate || '');
            setDesiredDeliveryDate(parsedDraft.desiredDeliveryDate || '');
            setRecurring(parsedDraft.recurring || {
               isRecurring: false,
               frequency: '',
               start_date: '',
               end_date: '',
            });
            setMaterialType(parsedDraft.materialType || '');
            setMaterialGrade(parsedDraft.materialGrade || '');
            setShowInsuranceValue(parsedDraft.showInsuranceValue || false);
            setInsuranceRequired(parsedDraft.insuranceRequired || false);
            setInsuranceValue(parsedDraft.insuranceValue || false);
            setHazardousMaterials(parsedDraft.hazardousMaterials || false);
            setSpecialHandling(parsedDraft.specialHandling || []);
            setPickupContactPhoneNumber(parsedDraft.pickupContactPhoneNumber || null);
            setPickupContactEmail(parsedDraft.pickupContactEmail || '');
            setDropoffContactPhoneNumber(parsedDraft.dropoffContactPhoneNumber || null);
            setDropoffContactEmail(parsedDraft.dropoffContactEmail || '');
            setVehicles(parsedDraft.vehicles || []);
            
            // Convert ISO date strings back to Date objects
            if (parsedDraft.pickupDateTime) {
               setPickupDateTime(new Date(parsedDraft.pickupDateTime));
            }
            if (parsedDraft.dropoffDateTime) {
               setDropoffDateTime(new Date(parsedDraft.dropoffDateTime));
            }
         } catch (error) {
            console.error('Error parsing saved form data:', error);
            // If there's an error parsing, we'll just use the default values
         }
      }
   }, []); // Empty dependency array means this runs once on mount
   
   const resetContext = () => {
      localStorage.removeItem('quoteDraft');
      setTabValue(0);
      setQuotes([]);
      setDidSearch(false);
      setIsPosting(false);
      setPickupLocation('');
      setDropoffLocation('');
      setVehicleYear(null)
      setVehicleVIN('');
      setVehicleType('');
      setVesselType('');
      setDeliverySpeed('standard');
      setInsurance('standard');
      setTransportType('open');
      setVehicleCondition(true);
      setDimensions({ length: null, width: null, height: null, beamWidth: null });
      setDesiredPickupDate('');
      setDesiredDeliveryDate('');
      setRecurring({
         isRecurring: false,
         frequency: '',
         start_date: '',
         end_date: '',
      });
      setMaterialType('');
      setMaterialGrade('');
      setShowInsuranceValue(false);
      setInsuranceRequired(false);
      setInsuranceValue(false);
      setHazardousMaterials(false);
      setSpecialHandling([]);
      setSnackbar({
         open: false,
         message: '',
         severity: 'info',
      });
   };
   
   // Prefill context from URL parameters
   useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      
      const paramMap = [
         { key: 'pickupLocation', setter: setPickupLocation },
         { key: 'dropoffLocation', setter: setDropoffLocation },
         { key: 'vehicleVIN', setter: setVehicleVIN },
         { key: 'vehicleType', setter: setVehicleType },
         { key: 'vehicleMake', setter: setVehicleMake },
         { key: 'vehicleModel', setter: setVehicleModel },
         { key: 'vehicleMakeModel', setter: setVehicleMakeModel },
         { key: 'vesselType', setter: setVesselType },
         { key: 'deliverySpeed', setter: setDeliverySpeed },
         { key: 'insurance', setter: setInsurance },
         { key: 'transportType', setter: setTransportType },
         { key: 'vehicleCondition', setter: setVehicleCondition },
         { key: 'desiredPickupDate', setter: setDesiredPickupDate },
         { key: 'desiredDeliveryDate', setter: setDesiredDeliveryDate },
         { key: 'pickupContactPhoneNumber', setter: setPickupContactPhoneNumber },
         { key: 'pickupContactEmail', setter: setPickupContactEmail },
         { key: 'dropoffContactPhoneNumber', setter: setDropoffContactPhoneNumber },
         { key: 'dropoffContactEmail', setter: setDropoffContactEmail },
      ];
      
      paramMap.forEach(({ key, setter }) => {
         const value = params.get(key);
         if (value) {
            setter(value);
         }
      });
      
      // Numeric conversion for vehicleYear
      const vehicleYearParam = params.get('vehicleYear');
      if (vehicleYearParam) {
         setVehicleYear(Number(vehicleYearParam));
      }
      
      // Dimensions from URL params (length, width, height, beamWidth)
      const dim = {
         length: params.get('length') ? Number(params.get('length')) : null,
         width: params.get('width') ? Number(params.get('width')) : null,
         height: params.get('height') ? Number(params.get('height')) : null,
         beamWidth: params.get('beamWidth') ? Number(params.get('beamWidth')) : null,
      };
      if (dim.length || dim.width || dim.height || dim.beamWidth) {
         setDimensions(dim);
      }
      
      // Recurring settings
      const isRecurringParam = params.get('isRecurring');
      if (isRecurringParam !== null) {
         setRecurring(prev => ({ ...prev, isRecurring: isRecurringParam === 'true' }));
      }
      const frequency = params.get('frequency');
      if (frequency) {
         setRecurring(prev => ({ ...prev, frequency }));
      }
      const start_date = params.get('start_date');
      if (start_date) {
         setRecurring(prev => ({ ...prev, start_date }));
      }
      const end_date = params.get('end_date');
      if (end_date) {
         setRecurring(prev => ({ ...prev, end_date }));
      }
      
      // DateTime conversion
      const pickupDateTimeParam = params.get('pickupDateTime');
      if (pickupDateTimeParam) {
         setPickupDateTime(new Date(pickupDateTimeParam));
      }
      const dropoffDateTimeParam = params.get('dropoffDateTime');
      if (dropoffDateTimeParam) {
         setDropoffDateTime(new Date(dropoffDateTimeParam));
      }
   }, []);
   
   useEffect(() => {
      // Update tabValue when defaultMode changes
      setTabValue(defaultMode === 'Auto' ? 0 : 1);
   }, [defaultMode]);
// Auto-save form draft to local storage on any change
   useEffect(() => {
      const draft = {
         tabValue,
         quotes,
         didSearch,
         isPosting,
         pickupLocation,
         dropoffLocation,
         vehicleVIN,
         vehicleType,
         vehicleYear,
         vehicleMake,
         vehicleModel,
         vehicleMakeModel,
         vesselType,
         deliverySpeed,
         insurance,
         transportType,
         vehicleCondition,
         dimensions,
         desiredPickupDate,
         desiredDeliveryDate,
         recurring,
         materialType,
         materialGrade,
         showInsuranceValue,
         insuranceRequired,
         insuranceValue,
         hazardousMaterials,
         specialHandling,
         pickupContactPhoneNumber,
         pickupContactEmail,
         dropoffContactPhoneNumber,
         dropoffContactEmail,
         pickupDateTime, // Will be stored as ISO strings
         dropoffDateTime, // Will be stored as ISO strings
         vehicles,
      };
      
      // Convert Date objects to ISO strings for serialization
      draft.pickupDateTime = draft.pickupDateTime instanceof Date ? draft.pickupDateTime.toISOString() : draft.pickupDateTime;
      draft.dropoffDateTime = draft.dropoffDateTime instanceof Date ? draft.dropoffDateTime.toISOString() : draft.dropoffDateTime;
      localStorage.setItem('quoteDraft', JSON.stringify(draft));
   }, [
      tabValue,
      quotes,
      didSearch,
      isPosting,
      pickupLocation,
      dropoffLocation,
      vehicleVIN,
      vehicleType,
      vehicleYear,
      vehicleMake,
      vehicleModel,
      vehicleMakeModel,
      vesselType,
      deliverySpeed,
      insurance,
      transportType,
      vehicleCondition,
      dimensions,
      desiredPickupDate,
      desiredDeliveryDate,
      recurring,
      materialType,
      materialGrade,
      showInsuranceValue,
      insuranceRequired,
      insuranceValue,
      hazardousMaterials,
      specialHandling,
      pickupContactPhoneNumber,
      pickupContactEmail,
      dropoffContactPhoneNumber,
      dropoffContactEmail,
      pickupDateTime,
      dropoffDateTime,
      vehicles,
   ]);
   
   const value = {
      tabValue,
      setTabValue,
      isPosting,
      setIsPosting,
      pickupLocation,
      setPickupLocation,
      dropoffLocation,
      setDropoffLocation,
      vehicleType,
      setVehicleType,
      deliverySpeed,
      setDeliverySpeed,
      insurance,
      setInsurance,
      transportType,
      setTransportType,
      vehicleCondition,
      setVehicleCondition,
      vehicleVIN, setVehicleVIN,
      vesselType, setVesselType,
      quotes, setQuotes,
      snackbar, setSnackbar,
      dimensions, setDimensions,
      desiredPickupDate, setDesiredPickupDate,
      desiredDeliveryDate, setDesiredDeliveryDate,
      recurring, setRecurring,
      materialType, setMaterialType,
      materialGrade, setMaterialGrade,
      showInsuranceValue, setShowInsuranceValue,
      insuranceRequired, setInsuranceRequired,
      insuranceValue, setInsuranceValue,
      specialHandling, setSpecialHandling,
      hazardousMaterials, setHazardousMaterials,
      vehicleYear, setVehicleYear,
      vehicleMake, setVehicleMake,
      vehicleModel, setVehicleModel,
      vehicleMakeModel, setVehicleMakeModel,
      didSearch, setDidSearch,
      pickupContactPhoneNumber, setPickupContactPhoneNumber,
      pickupContactEmail, setPickupContactEmail,
      dropoffContactPhoneNumber, setDropoffContactPhoneNumber,
      dropoffContactEmail, setDropoffContactEmail,
      pickupDateTime, setPickupDateTime,
      dropoffDateTime, setDropoffDateTime,
      vehicles, setVehicles,
      resetContext
   };

   return (
      <QuoteContext.Provider value={value}>
         {children}
      </QuoteContext.Provider>
   );
};
